import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { fadeUp, stagger } from "./motion";
import { SPECIES_CATALOG } from "../data/species";
import { localizeSpecies } from "../data/speciesTranslations";
import { useLang } from "../i18n";

const MAX = 4;
const MAX_HARDNESS = Math.max(
  ...SPECIES_CATALOG.map((s) => parseInt(String(s.properties.hardness).replace(/[^0-9]/g, ""), 10) || 0)
);

const num = (str) => parseInt(String(str).replace(/[^0-9]/g, ""), 10) || 0;

export const SpeciesCompare = () => {
  const { t, lang } = useLang();
  const [picked, setPicked] = useState(["white-oak", "black-walnut", "hard-maple"]);

  const toggle = (slug) => {
    setPicked((p) => {
      if (p.includes(slug)) return p.length > 1 ? p.filter((s) => s !== slug) : p;
      if (p.length >= MAX) return [...p.slice(1), slug];
      return [...p, slug];
    });
  };

  const selected = picked
    .map((slug) => SPECIES_CATALOG.find((s) => s.slug === slug))
    .filter(Boolean)
    .map((s) => localizeSpecies(s, lang));

  const rows = [
    { key: "type", label: t("compare.type"), render: (s) => (s.type === "Hardwood" ? t("catalog.Hardwoods") : t("catalog.Softwoods")) },
    { key: "density", label: t("compare.density"), render: (s) => s.properties.density },
    {
      key: "hardness",
      label: t("compare.hardness"),
      render: (s) => (
        <div>
          <span>{s.properties.hardness}</span>
          <div className="mt-2 h-1 w-full rounded-full bg-brand-blue/10">
            <div
              className="h-1 rounded-full bg-brand-timber transition-[width] duration-500"
              style={{ width: `${Math.min(100, (num(s.properties.hardness) / MAX_HARDNESS) * 100)}%` }}
            />
          </div>
        </div>
      ),
    },
    { key: "workability", label: t("compare.workability"), render: (s) => s.properties.workability },
    {
      key: "uses",
      label: t("compare.uses"),
      render: (s) => (
        <div className="flex flex-wrap gap-1.5">
          {s.uses.slice(0, 4).map((u) => (
            <span key={u} className="rounded-full border border-brand-blue/20 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-brand-blue">
              {u}
            </span>
          ))}
        </div>
      ),
    },
  ];

  return (
    <section
      id="compare"
      data-testid="species-compare"
      className="border-y border-border bg-secondary/60 py-28 md:py-36"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="mb-12 flex flex-col justify-between gap-8 md:flex-row md:items-end"
        >
          <div>
            <motion.span variants={fadeUp} className="font-mono text-xs uppercase tracking-[0.25em] text-brand-timber">
              {t("compare.kicker")}
            </motion.span>
            <motion.h2 variants={fadeUp} className="mt-4 max-w-2xl font-serif text-5xl font-medium leading-[0.95] tracking-tight text-brand-blue md:text-6xl">
              {t("compare.title")}
            </motion.h2>
          </div>
          <motion.p variants={fadeUp} className="max-w-sm font-light text-base leading-relaxed text-brand-slate">
            {t("compare.sub")}
          </motion.p>
        </motion.div>

        {/* Picker chips */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-10"
        >
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-slate">
            {t("compare.pick")}
          </p>
          <div className="flex flex-wrap gap-2">
            {SPECIES_CATALOG.map((s) => {
              const on = picked.includes(s.slug);
              return (
                <button
                  key={s.slug}
                  data-testid={`compare-chip-${s.slug}`}
                  onClick={() => toggle(s.slug)}
                  className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-wider transition-colors duration-300 ${
                    on
                      ? "border-brand-blue bg-brand-blue text-brand-bone"
                      : "border-brand-blue/25 text-brand-blue hover:border-brand-timber hover:text-brand-timber"
                  }`}
                >
                  {s.name}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Comparison table */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="relative"
          data-testid="compare-table"
        >
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-brand-bone to-transparent lg:hidden" />
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left">
            <thead>
              <tr>
                <th className="w-40 border-b border-border pb-4 align-bottom font-mono text-[11px] font-normal uppercase tracking-[0.2em] text-brand-slate" />
                {selected.map((s) => (
                  <th key={s.slug} data-testid={`compare-col-${s.slug}`} className="border-b border-border pb-4 pr-6 align-bottom">
                    <Link to={`/species/${s.slug}`} className="group block">
                      <div className="mb-3 h-24 w-full max-w-[220px] overflow-hidden rounded-sm border border-border">
                        <img src={s.gallery?.[0]?.src || s.image} alt={s.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <span className="font-serif text-2xl font-medium text-brand-blue transition-colors group-hover:text-brand-timber md:text-3xl">
                        {s.name}
                      </span>
                      <p className="font-mono text-[10px] italic text-brand-slate">{s.latin}</p>
                    </Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} data-testid={`compare-row-${r.key}`}>
                  <td className="border-b border-border py-5 pr-4 align-top font-mono text-[11px] uppercase tracking-[0.2em] text-brand-timber">
                    {r.label}
                  </td>
                  {selected.map((s) => (
                    <td key={s.slug} className="border-b border-border py-5 pr-6 align-top font-light text-brand-blue">
                      {r.render(s)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
